import { useMemo } from 'react'

import { ShiftManagementSelectableEmployeesDocument } from 'generated/resources/graphql'
import type { ShiftManagementSelectableEmployeesQuery } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

type PermissionAction = 'read' | 'create'

export type SelectableEmployee = Pick<
  ShiftManagementSelectableEmployeesQuery['shiftManagement']['employeesPermissions'][number],
  'id' | 'fullName'
>

// Employees USING SHIFT MANAGEMENT + that I have permissions to read/create shifts
const useSelectableEmployees = (
  startAt: string,
  endAt: string,
  action: PermissionAction = 'create',
  locationIds?: string[]
): {
  loading: boolean
  selectableEmployeeIds: string[]
  selectableEmployees: SelectableEmployee[]
} => {
  const isRead = action === 'read'
  // Cache-first to avoid fetching the same data multiple times
  // because SelectableEmployees is stable and doesn't change frequently.
  //
  // This returns employees using shiftmanagement, but I might not have permissions for them
  const { data: selectableEmployeesData, loading: selectableEmployeesLoading } = useQuery(
    ShiftManagementSelectableEmployeesDocument,
    {
      fetchPolicy: 'cache-first',
      variables: {
        startAt,
        endAt,
        locationIds,
      },
    }
  )

  const { data: terminatedEmployeesData, loading: terminatedEmployeesLoading } = useQuery(
    ShiftManagementSelectableEmployeesDocument,
    {
      fetchPolicy: 'cache-first',
      variables: {
        startAt,
        endAt,
        terminated: true,
        locationIds,
      },
      skip: !isRead,
    }
  )

  const employeesPermissions = selectableEmployeesData?.shiftManagement.employeesPermissions

  const [selectableEmployees, selectableEmployeeIds] = useMemo(() => {
    const ids: string[] = []
    const employees =
      employeesPermissions?.reduce<SelectableEmployee[]>((acc, employee) => {
        if (isRead || employee.permissions?.create) {
          ids.push(employee.id)
          acc.push({ id: employee.id, fullName: employee.fullName })
        }
        return acc
      }, []) ?? []
    return [employees, ids]
  }, [employeesPermissions, isRead])

  const terminatedEmployeeIds = useMemo(
    () =>
      terminatedEmployeesData?.shiftManagement.employeesPermissions?.reduce<string[]>(
        (acc, employee) => {
          if (isRead) acc.push(employee.id)
          return acc
        },
        []
      ) ?? [],
    [terminatedEmployeesData, isRead]
  )

  const terminatedEmployees = useMemo(
    () =>
      terminatedEmployeesData?.shiftManagement.employeesPermissions?.reduce<SelectableEmployee[]>(
        (acc, employee) => {
          if (isRead) acc.push({ id: employee.id, fullName: employee.fullName })
          return acc
        },
        []
      ) ?? [],
    [terminatedEmployeesData, isRead]
  )

  const allSelectableEmployeeIds = useMemo(
    () => [...selectableEmployeeIds, ...terminatedEmployeeIds],
    [selectableEmployeeIds, terminatedEmployeeIds]
  )

  const allSelectableEmployees = useMemo(
    () => [...selectableEmployees, ...terminatedEmployees],
    [selectableEmployees, terminatedEmployees]
  )

  return {
    loading: selectableEmployeesLoading || terminatedEmployeesLoading,
    selectableEmployeeIds: allSelectableEmployeeIds,
    selectableEmployees: allSelectableEmployees,
  }
}

export { useSelectableEmployees }
